/**
 * Composer 작성 폼 조립 렌더러 — **갈래 하나**(`ComposerWidgetTreeRenderer`).
 *
 * 작성 폼은 `ComposerWidget[]` 트리 하나로만 렌더한다. 그 트리의 출처는 둘이지만(저작
 * `template.composerWidgetTree` / 본문 바인딩 파생 `deriveWriteForm`) **렌더러는 하나**다 —
 * 호스트가 `저작트리 ?? 파생노드`를 한 값으로 합쳐 `tree`로 넘긴다.
 *
 * ⚠️ 2026-09-30(S3-b): 종전 이 파일이 함께 갖고 있던 flat 갈래(`export default WriteFormRenderer`,
 *   입력 `InputWidget[]`)는 **삭제됐다**(결정 `2026-09-30-writeform-to-composer-widget-tree-consolidation`).
 *   S2에서 `deriveWriteForm`이 `InputWidget[]`(= `kind:'input'` 트리 노드)를 직접 내게 된 뒤로
 *   두 갈래의 입력 타입이 같아져, 파생 배열을 그대로 `tree`에 넘기면 **같은 DOM**이 나온다:
 *     · `InputWidget`은 `ComposerWidget` union 멤버라 할당에 변환층이 필요 없다.
 *     · 최상위(root)는 `gridded=false`라 트리 경로의 래퍼도 `<Fragment>` 그대로이고, 컨테이너는
 *       양쪽 다 `<div className="space-y-4">`였다.
 *     · 파생 노드엔 dataSourceRows 위젯이 없어 `placedKeys`가 비고 → footer가 편집형 list 선언
 *       **전부**를 옛 flat 갈래와 같은 순서로 append한다.
 *   그래서 이 제거는 **기능 손실이 아니라 증가**다 — 파생 경로가 트리 경로의 host 채널
 *   (photo 업로드·registered 폼 위젯 레지스트리·서명 슬롯·검색 제안·모달 열기)을 함께 얻는다.
 *   되살리지 말 것: 갈래가 둘이면 렌더와 검증이 서로 다른 값을 읽기 시작한다(그것이 이번에
 *   함께 걷은 `collectMissingRequiredNodes` 짝의 정체다).
 * - AI 제안: 옛 flat writeFormExtras(aiSelect) 인라인 블록은 2026-07-25 철거(실사용 0, 트리
 *   sortableSelect 위젯으로 대체). 재도입 시 등록형 위젯으로 트리에 얹는다.
 *
 * ⚠️ 순수 입력 위젯 렌더(renderWriteNode)와 트리 walk(ComposerWidgetTreeView)는
 *    `@safetyhub/report-composer`로 이전(web ↔ admin 공유). dataSources 행 그리드
 *    (RowsCollectionEditor/DataSourceRowsField)도 rc로 이동(2026-07-20) — admin 캔버스
 *    라이브 모드와 같은 컴포넌트를 주입한다(WYSIWYG 패리티). 트리 렌더러는 footer slot 주입.
 *
 * 이 조립 렌더러 자체도 rc 정본(2026-07-26 web → rc 승격) — admin 시뮬레이션이 web 배포판과
 * 동일한 작성 화면을 렌더해야 하므로(호스트별 재조립 금지). web은 재수출층만 유지.
 */
import { type ReactNode } from 'react';
import type {
  ComposerSuggestSearch,
  ComposerSuggestSelect,
  ComposerWidget,
  DataDef,
  DataSourceDef,
} from '@safetyhub/shared';
import { ROWS_SEED_SUFFIX } from '@safetyhub/shared';
import { ComposerWidgetTreeView } from './composerWidgetTreeRender';
import type {
  ComposerPhotoUpload,
  ComposerRegisteredFormWidget,
  DataSourceRowsRenderer,
} from './composerWidgetTreeRender';
import type { ComposerSignatureSlotsContext } from './SignatureSlotView';
import { useActiveComposerView } from './activeComposerView';
import {
  collectPlacedDataSourceRowsKeys,
  editableListDataSources,
} from './dataSourceWidgets';
import { DataSourceRowsField } from './RowsCollectionEditor';
import type { RegisteredRowWidget } from './RowsCollectionEditor';
import type { RowObject } from './dataSourceCollection';
import type { ComposerImprovementImageHost } from './widgets/improvementImageHost';

// ── 작성 페이지 위젯 트리 렌더러 (composerWidgetTree / 파생 InputWidget[] 공용 소비) ──
// 순수 트리 walk는 패키지 ComposerWidgetTreeView로 위임하고, dataSources(편집형 list 선언)의 행 그리드
// 렌더(rc DataSourceRowsField — admin 캔버스 라이브 모드와 공유)를 footer slot으로 주입한다.
// 결정: 2026-07-08-composer-writeform-editing-structure §3(반응형=렌더러 책임)

/**
 * `ComposerWidget[]`을 반응형 작성 폼으로 렌더 — 작성 폼의 **유일한** 렌더러(2026-09-30 S3-b).
 *
 * `tree`의 출처는 둘이다(호스트가 하나로 합쳐 넘긴다):
 *   ① 저작 트리 `template.composerWidgetTree` — 그룹/제목/구분선·표·반복·모달을 갖춘 전체 형상.
 *   ② 본문 바인딩 파생 `deriveWriteForm(template)` — `kind:'input'` 노드만 담긴 평평한 배열
 *      (트리를 저작하지 않은 템플릿). `InputWidget`이 `ComposerWidget` union 멤버라 변환층이 없다.
 * ②는 저작 전용 축(`suggestionsFrom`·`actionItem`·`enabledWhen`·`visibleWhen`·`hidden`)을 **한 번도
 * 채우지 않으므로**(writeForm.ts에 그 키가 0건) 이 렌더러의 그 분기들은 ②에서 inert다 — 즉
 * ②를 여기로 합친 것이 동작을 바꾸지 않는다.
 *
 * hidden 입력은 렌더 안 함(renderWriteNodeCore가 null).
 * dataSources(편집형 list 선언)는 트리에 **배치되지 않은 것만** 트리 뒤에 행 그리드로 append(footer
 * 주입). ②는 dataSourceRows 위젯을 담지 않으니 배치분이 0이라 선언 전부가 footer로 간다.
 * ⚠️ AI 제안(aiSuggest)은 트리 모델에서 제외(2026-07-08) — 향후 커스텀(등록형) 위젯으로 재도입.
 *    옛 flat writeFormExtras + AiSelectBlock 경로는 2026-07-25 철거됐다.
 */
export function ComposerWidgetTreeRenderer({
  tree,
  values,
  onChange,
  registeredWidgets,
  registeredFormWidgets,
  registeredWidgetContext,
  dataSources,
  dataDefs,
  resolvedRows,
  onCatalogRowSubmit,
  onSuggestSearch,
  onSuggestSelect,
  onPhotoUpload,
  signatureSlots,
  initialValuesReady,
  onImprovementImage,
}: {
  tree: ComposerWidget[];
  values: Record<string, unknown>;
  onChange: (bindingKey: string, value: unknown) => void;
  /** 행 그리드(dataSource) 등록형 위젯 레지스트리(DataSourceRowsField 주입용). */
  registeredWidgets?: Record<string, RegisteredRowWidget>;
  /** 트리 폼 등록형 위젯(widget:'registered') 레지스트리 — ComposerWidgetTreeView에 주입. */
  registeredFormWidgets?: Record<string, ComposerRegisteredFormWidget>;
  /** 등록형 위젯 렌더 컨텍스트(현재 현장/회사 id) — ComposerWidgetTreeView에 전달. */
  registeredWidgetContext?: { companyId?: string; siteId?: string };
  dataSources?: DataSourceDef[];
  /** 내장 데이터셋 선언 — 표 구분 그룹 틀 자동 파생(rc로 전달). */
  dataDefs?: DataDef[];
  /**
   * host가 런타임 resolve한 후보 rows(소스 키별) — sortableSelect rows 최우선 채널(P3, rc로 전달).
   * composed 작성 페이지가 AI 개선 의견 후보를 여기로 주입해 모달 안 sortableSelect가 소비한다.
   */
  resolvedRows?: Record<string, Record<string, unknown>[]>;
  /**
   * 카탈로그 행 즉시 등록 호스트 콜백(submitAction 모달 — rc ComposerWidgetTreeView로 전달).
   * 미배선 페이지(external 무인증 등)에선 submitAction 모달을 여는 콤보 액션 항목이 숨겨진다.
   */
  onCatalogRowSubmit?: (
    catalogKey: string,
    values: Record<string, string>,
  ) => Promise<Record<string, unknown> | null>;
  /**
   * 범용 검색 제안 축(suggestionsFrom) 호스트 검색 콜백 — rc ComposerWidgetTreeView로 전달.
   * 미배선 페이지(external 무인증 등)는 제안 없이 일반 입력 degrade.
   * 결정: 2026-08-05-composer-suggestion-source-axis.
   */
  onSuggestSearch?: ComposerSuggestSearch;
  /** 제안 행 선택 확정 통지(usage 증가 등 호스트 부수효과) — rc로 전달(fire-and-forget). */
  onSuggestSelect?: ComposerSuggestSelect;
  /**
   * photo 위젯 업로드 호스트 콜백(파일 업로드 → 저장 URL 반환) — rc ComposerWidgetTreeView로 전달.
   * 미배선 페이지(external 무인증 등)는 안내 placeholder로 degrade.
   */
  onPhotoUpload?: ComposerPhotoUpload;
  /**
   * 서명 슬롯 host 채널(P2) — 걷은 서명 맵 + 탭/지우기 콜백. 트리(반복 위젯·formTable 반복 행)와
   * 행 그리드(DataSourceRowsField) 양쪽에 전달. 미배선 페이지(생성 모드·external)는 표시 전용.
   */
  signatureSlots?: ComposerSignatureSlotsContext;
  /**
   * 초기값(prefill 포함) 준비 완료 신호 — collapseWhenFilled 섹션의 "채워져 있으면 접힘" 판정
   * 시점(rc ComposerWidgetTreeView로 전달). prefill을 비동기 로드하는 페이지는 settle 후 true.
   */
  initialValuesReady?: boolean;
  /**
   * 개선 이미지 AI 호스트 콜백(등록형 행 위젯 `improvement-image` — 생성. 유사 검색은 2026-09-14 제거,
   * widgets/improvementImageHost.ts 헤더 참조). 트리 등록형
   * 컨텍스트(registeredWidgetContext)와 행 그리드 컨텍스트(rowWidgetContext) 양쪽의 `improvementImage`
   * 키에 접어 넘긴다 — 등록형 위젯의 유일한 채널이 context라 onPhotoUpload처럼 별도 prop으로 관통하지
   * 않는다. 미배선 페이지(external 무인증 등)는 이미지 표시 전용으로 degrade.
   */
  onImprovementImage?: ComposerImprovementImageHost;
}) {
  // 실폼 반응형 뷰 자동 판정(현재 뷰포트 폭·회전 → 뷰별 오버라이드 반영). Phase 4.
  const view = useActiveComposerView();

  // 편집형 list 데이터셋 한 개를 그 위치/footer에서 동일하게 행 그리드로 렌더하는 공용 헬퍼.
  // rc 공유 DataSourceRowsField — admin 캔버스 라이브 모드와 같은 컴포넌트(WYSIWYG 패리티).
  const renderRowsFor = (def: DataSourceDef, value: unknown): ReactNode => (
    <DataSourceRowsField
      key={`ds-${def.key}`}
      def={def}
      value={value}
      seed={values[def.key + ROWS_SEED_SUFFIX]}
      registeredWidgets={registeredWidgets}
      // 등록형 행 위젯의 작성 화면 컨텍스트(admin 캔버스 주입과 동일 형상 — WYSIWYG).
      // improvementImage는 개선 이미지 호스트 콜백(트리 registeredWidgetContext와 같은 키).
      rowWidgetContext={{ values, resolvedRows, improvementImage: onImprovementImage }}
      signatureSlots={signatureSlots}
      // widget='photo' 행 필드 업로드 — 트리 photo 위젯과 같은 호스트 콜백 한 벌(onPhotoUpload).
      uploadPhoto={onPhotoUpload}
      onChange={(nextRows: RowObject[]) => onChange(def.key, nextRows)}
    />
  );

  // 트리에 배치된 dataSourceRows 위젯 → 그 위치에서 def를 bindingKey로 조회해 행 그리드로 렌더(host 주입).
  const dataSourceRowsRenderer: DataSourceRowsRenderer = (widget, value) => {
    const def = (dataSources ?? []).find((d) => d.key === widget.bindingKey);
    if (!def) return null;
    return renderRowsFor(def, value);
  };

  // footer fallback: 트리에 **배치되지 않은** 편집형 list 데이터셋만 트리 뒤에 append(회귀0 — 배치분은 제외).
  const placedKeys = collectPlacedDataSourceRowsKeys(tree);
  const footerDefs = editableListDataSources(dataSources).filter((def) => !placedKeys.has(def.key));
  const footer =
    footerDefs.length > 0 ? (
      <>{footerDefs.map((def) => renderRowsFor(def, values[def.key]))}</>
    ) : undefined;

  return (
    <ComposerWidgetTreeView
      tree={tree}
      values={values}
      onChange={onChange}
      footer={footer}
      view={view}
      dataSources={dataSources}
      dataDefs={dataDefs}
      resolvedRows={resolvedRows}
      onCatalogRowSubmit={onCatalogRowSubmit}
      onSuggestSearch={onSuggestSearch}
      onSuggestSelect={onSuggestSelect}
      onPhotoUpload={onPhotoUpload}
      signatureSlots={signatureSlots}
      registeredFormWidgets={registeredFormWidgets}
      // 개선 이미지 호스트 콜백을 등록형 컨텍스트에 접는다 — 배선이 없으면 형상 그대로(회귀 0).
      registeredWidgetContext={
        onImprovementImage
          ? { ...registeredWidgetContext, improvementImage: onImprovementImage }
          : registeredWidgetContext
      }
      dataSourceRowsRenderer={dataSourceRowsRenderer}
      initialValuesReady={initialValuesReady}
    />
  );
}
